import SectionHeader from './SectionHeader';

const DIMENSIONS = [
  {
    no: '01',
    name: '视觉表现尺度',
    desc: '服装暴露程度、拍摄机位与动作姿态是否刻意制造歧义，是机器初审最先扫的维度。',
    examples: ['透视 / 局部特写机位', '过度贴身的姿势摆拍', '遮挡物随时会掉落的强暗示'],
  },
  {
    no: '02',
    name: '文案话术暗示',
    desc: '文字与标题常是「擦边」的判定放大器，谐音、缩写、引导句都会拉高触发概率。',
    examples: ['谐音字绕关键词', '私聊 / 群聊引导用语', '夸张吸睛型标题党'],
  },
  {
    no: '03',
    name: '行为动作引导',
    desc: '评论区、连麦、直播动作是否往「越线」方向引导，行为类判定通常比静态画面更重。',
    examples: ['连麦时做越界动作', '暗示性手势与口型', '剧情向但内容擦边'],
  },
  {
    no: '04',
    name: '语境与未成年人风险',
    desc: '同一画面放在不同语境里结果完全不同；一旦触发未成年保护逻辑，直接按最重档处理。',
    examples: ['账号定位与内容不匹配', '容易被未成年看到的高频推流', '诱导点击的封面组合'],
  },
];

const LADDER = [
  { level: '提醒', color: 'bg-secondary-500', icon: 'ri-information-line', desc: '首次轻微触发，限时自查，一般不下架。' },
  { level: '限流', color: 'bg-accent-500', icon: 'ri-speed-line', desc: '降低推荐与曝光，仅自己 / 少数粉丝可见。' },
  { level: '整改', color: 'bg-primary-500', icon: 'ri-tools-line', desc: '要求修改后再发布，未改前内容不可见。' },
  { level: '下架', color: 'bg-primary-700', icon: 'ri-archive-line', desc: '判定违规直接下架，进入记录与申诉流程。' },
  { level: '封禁', color: 'bg-foreground-950', icon: 'ri-shield-cross-line', desc: '账号维度处罚，短则数日、重则永久封禁。' },
];

export default function BoundaryScale() {
  return (
    <section id="boundary" className="bg-background-100/70 py-14 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:gap-12">
          <div>
            <SectionHeader
              eyebrow="51擦边 · 尺度边界"
              title="越没越线，平台到底看什么"
              desc="审核标准不是拍脑袋。机器初筛 + 人工复核双轨并行，围绕四个维度给内容打分。先弄清楚这些维度，发布前的很多疑问能少一半。"
            />
            <div className="mt-6 rounded-2xl border border-accent-200 bg-background-50 p-5">
              <p className="flex items-center gap-2 text-sm font-bold text-accent-800">
                <i className="ri-lightbulb-line text-lg" />
                一个朴素结论
              </p>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                大多数被误判或硬扛「擦边」的情况，不是单一画面出错，而是视觉 + 文案 + 语境同时踩线。单一维度轻微，通常只到限流这一档。
              </p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {DIMENSIONS.map((d) => (
              <article key={d.no} className="rounded-2xl border border-background-200 bg-background-50 p-5">
                <div className="flex items-center justify-between">
                  <span className="font-serif text-sm font-black text-primary-400">{d.no}</span>
                  <i className="ri-arrow-right-up-line text-accent-500" />
                </div>
                <h3 className="mt-1 font-serif text-lg font-bold text-foreground-950">{d.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{d.desc}</p>
                <ul className="mt-3 space-y-1.5">
                  {d.examples.map((e) => (
                    <li key={e} className="flex items-start gap-2 text-xs text-foreground-700">
                      <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600" />
                      {e}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>

        {/* 处罚阶梯 */}
        <div className="mt-14 rounded-3xl bg-foreground-950 px-6 py-8 md:px-10 md:py-10">
          <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-accent-300">
                <i className="ri-stairs-line" />
                违规处理阶梯
              </p>
              <h3 className="mt-2 font-serif text-xl font-black text-background-50 md:text-3xl">
                从一次提醒到永久封禁，中间隔了 5 档
              </h3>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-background-50/60">
              同一内容触发严重程度不同，处罚档位也不同。越靠后说明性质越重，也越难靠「整改」挽回。
            </p>
          </div>

          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {LADDER.map((l, i) => (
              <div key={l.level} className="relative rounded-2xl border border-background-50/10 bg-background-50/5 p-4">
                <div className="flex items-center gap-2">
                  <span className={`flex h-8 w-8 items-center justify-center rounded-full ${l.color} text-background-50`}>
                    <i className={`${l.icon} text-sm`} />
                  </span>
                  <span className="font-serif text-sm font-bold text-background-50">0{i + 1}</span>
                </div>
                <p className="mt-3 text-base font-bold text-background-50">{l.level}</p>
                <p className="mt-1.5 text-xs leading-relaxed text-background-50/60">{l.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}